import { useCallback, useEffect, useMemo, useState } from "react"
import { useActiveSessions } from "@/common/queries/use-active-sessions"
import { useFetchLocation } from "@/common/queries/use-fetch-location"
import { get, isFunction } from "radash"
import { Dialog, Modal } from "react-aria-components"

import { useTranslation } from "@be-miniapp/i18n"
import { log } from "@be-miniapp/logging"
import {
  hasActiveTransportSession,
  hasResolvedRideLocation,
  RIDE_LOCATION_CACHE_TTL_MS,
} from "@be-miniapp/shared"
import {
  LOCATION_POPUP_REASON,
  useLocationPopupSetting,
} from "@be-miniapp/shared/stores/location-popup"
import { Backdrop } from "@be-miniapp/ui/components/backdrop"
import { Button } from "@be-miniapp/ui/components/ui/button"
import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"

import { PARTNER_IDENTIFIERS } from "@/constants/partner"
import { usePartnerInfo } from "@/store/partner-information"
import { useUserPersistingData } from "@/store/user-persisting-data"
import { cn } from "@/utils/cn"
import {
  initializeNativeLocationBridge,
  LOCATION_PERMISSION_RESPONSE_EVENT,
  registerNativeLocationGrantHandler,
} from "@/utils/native-location-permission"

initializeNativeLocationBridge()

// Define types for native bridge interfaces
interface NativeBridgeInterface {
  available: boolean
  postMessage: (message: string) => void
  messageHandler: (message: string) => void
}

interface FlutterInAppWebView {
  callHandler: (handlerName: string, message: string) => void
}

interface NativeBridgeRequest {
  action: string
  payload: Record<string, unknown>
  requestId: string
}

interface NativeBridgeResponse {
  status: "success" | "error"
  data?: unknown
  error?: string
  requestId: string
}

interface LocationPermissionResponseDetail {
  isGranted: boolean
}

// Extend Window interface with our custom properties
declare global {
  interface Window {
    NativeBridge: NativeBridgeInterface
    flutter_inappwebview: FlutterInAppWebView
    nativeBridgeResponseHandler: (responseJson: string) => void
    nativeBridgeMessageHandler: (message: string) => void
    webkit?: {
      messageHandlers?: {
        requestLocation?: {
          postMessage?: (message: string) => void
        }
      }
    }
    android?: {
      requestLocation?: () => void
    }
  }
}

/**
 * Call a Flutter native method with the specified action and payload
 * @param action The action to perform
 * @param payload Optional payload data
 * @returns Promise that resolves with the response data
 */
function callFlutter(
  action: string,
  payload: Record<string, unknown> = {}
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    if (!window.NativeBridge || !window.NativeBridge.postMessage) {
      reject(new Error("NativeBridge not ready"))
      return
    }

    const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
    const request: NativeBridgeRequest = {
      action,
      payload,
      requestId,
    }
    const message = JSON.stringify(request)

    // Setup response handler
    window.nativeBridgeResponseHandler = (responseJson: string) => {
      try {
        const response = JSON.parse(responseJson) as NativeBridgeResponse
        if (response.requestId !== requestId) return

        if (response.status === "success") {
          resolve(response.data)
        } else {
          reject(new Error(response.error || "Unknown error"))
        }
      } catch (error) {
        reject(
          new Error(
            `Parse error: ${error instanceof Error ? error.message : String(error)}`
          )
        )
      }
    }

    console.log(`JS sending: ${message}`)
    window.NativeBridge.postMessage(message)
  })
}

/**
 * Request location permissions from the native app
 */
async function requestLocation(): Promise<boolean> {
  const fetchLocationIOS = get(
    window,
    "webkit.messageHandlers.requestLocation.postMessage",
    null
  )
  const fetchLocationAndroid = get(window, "android.requestLocation", null)
  const fetchLocationFlutter = get(
    window,
    "flutter_inappwebview.callHandler",
    null
  )

  try {
    if (fetchLocationIOS && isFunction(fetchLocationIOS)) {
      window.webkit?.messageHandlers?.requestLocation?.postMessage?.("location")
      return true
    } else if (fetchLocationAndroid && isFunction(fetchLocationAndroid)) {
      window.android?.requestLocation?.()
      return true
    } else if (
      window.NativeBridge.available ||
      isFunction(fetchLocationFlutter)
    ) {
      window.NativeBridge.available = true
      await callFlutter("openAppSettings")
      return true
    } else {
      log({
        category: "location-permission",
        level: "error",
        message: "No location fetcher found",
      })
      return false
    }
  } catch (error) {
    console.error("Failed to request location", error)
    log({
      category: "location-permission",
      level: "error",
      message: `Failed to request location: ${error instanceof Error ? error.message : String(error)}`,
    })
    return false
  }
}

export function LocationPopup() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isAwaitingHostPermission, setIsAwaitingHostPermission] =
    useState(false)

  // Location popup state
  const showPopup = useLocationPopupSetting((state) => state.isVisible)
  const dismissPopup = useLocationPopupSetting((state) => state.dismiss)
  const popupReason = useLocationPopupSetting((state) => state.reason)

  // User data state
  const updateLocationPermission = useUserPersistingData(
    (state) => state.setLocationPermissionState
  )
  const hasHydrated = useUserPersistingData((state) => state._hasHydrated)
  const isManuallySet = useUserPersistingData(
    (state) => state.isLocationManuallyChanged
  )
  const persistedLocation = useUserPersistingData((state) => state.location)
  const persistedLocationAddress = useUserPersistingData(
    (state) => state.locationAddress
  )
  const locationPermissionStatus = useUserPersistingData(
    (state) => state.locationPermissionState.status
  )
  const lastLocationTimestamp = useUserPersistingData(
    (state) => state.lastLocationTimestamp
  )

  const { data: activeSessions } = useActiveSessions({
    data: {
      engagementId: "",
      currentState: "",
    },
  })

  // Partner info
  const partnerIdentifier = usePartnerInfo((state) => state.partner_identifier)

  const handleLocationAccepted = useCallback(() => {
    setIsAwaitingHostPermission(false)
    dismissPopup()
  }, [dismissPopup])

  const locationFetchOptions = useMemo(
    () => ({
      forceFresh: true,
      options: {
        onAccepted: handleLocationAccepted,
        onDenied: () => {
          setIsAwaitingHostPermission(false)
        },
        onUnAvailable: () => {
          setIsAwaitingHostPermission(false)
        },
      },
    }),
    [handleLocationAccepted]
  )

  // Location fetching state
  const { isFetching, fetch } = useFetchLocation(locationFetchOptions)

  // Manual override should only win for the recent ride-entry window.
  const isLocationExpired =
    isManuallySet &&
    lastLocationTimestamp != null &&
    lastLocationTimestamp < Date.now() - RIDE_LOCATION_CACHE_TTL_MS

  // Derived state
  const isNoCoordinates = popupReason === LOCATION_POPUP_REASON.NO_COORDINATES
  const hasResolvedLocation = hasResolvedRideLocation({
    location: persistedLocation,
    address: persistedLocationAddress,
    isLocationManuallyChanged: isManuallySet,
    lastLocationTimestamp,
    locationPermissionStatus,
  })
  const hasActiveTrip = hasActiveTransportSession(activeSessions)

  // Determine if we should show the popup
  const shouldShowPopup =
    hasHydrated &&
    showPopup &&
    locationPermissionStatus !== "accepted" &&
    !hasResolvedLocation &&
    !hasActiveTrip &&
    (!isManuallySet || isLocationExpired)

  useEffect(() => {
    if (showPopup && (hasResolvedLocation || hasActiveTrip)) {
      dismissPopup()
    }
  }, [dismissPopup, hasActiveTrip, hasResolvedLocation, showPopup])

  useEffect(() => {
    if (!shouldShowPopup || isAwaitingHostPermission) return

    if (!isManuallySet || isLocationExpired) {
      void fetch()
    }
  }, [
    fetch,
    isAwaitingHostPermission,
    isLocationExpired,
    isManuallySet,
    shouldShowPopup,
  ])

  useEffect(() => {
    const handleNativePermissionResponse = (event: Event) => {
      const { isGranted } =
        (event as CustomEvent<LocationPermissionResponseDetail>).detail ?? {}

      if (!isGranted) {
        setIsAwaitingHostPermission(false)
        return
      }

      setIsAwaitingHostPermission(true)
      void fetch().finally(() => {
        setIsAwaitingHostPermission(false)
      })
    }

    const unregisterGrantHandler = registerNativeLocationGrantHandler(() => {
      setIsAwaitingHostPermission(true)
      void fetch().finally(() => {
        setIsAwaitingHostPermission(false)
      })
    })

    window.addEventListener(
      LOCATION_PERMISSION_RESPONSE_EVENT,
      handleNativePermissionResponse
    )

    return () => {
      unregisterGrantHandler()
      window.removeEventListener(
        LOCATION_PERMISSION_RESPONSE_EVENT,
        handleNativePermissionResponse
      )
    }
  }, [fetch])

  useEffect(() => {
    if (!isAwaitingHostPermission) return

    const refreshLocationOnResume = () => {
      if (document.visibilityState && document.visibilityState !== "visible") {
        return
      }

      void fetch().finally(() => {
        setIsAwaitingHostPermission(false)
      })
    }

    window.addEventListener("focus", refreshLocationOnResume)
    document.addEventListener("visibilitychange", refreshLocationOnResume)

    return () => {
      window.removeEventListener("focus", refreshLocationOnResume)
      document.removeEventListener("visibilitychange", refreshLocationOnResume)
    }
  }, [fetch, isAwaitingHostPermission])

  if (!shouldShowPopup) {
    return null
  }

  const handleManualLocationClick = () => {
    // Clear history to prevent navigation issues
    window.history.replaceState(null, "", window.location.href)
    dismissPopup()
    navigate("/profile/edit_location")
  }

  const handleAutoLocationClick = () => {
    updateLocationPermission("not-asked")
    setIsAwaitingHostPermission(true)
    void requestLocation().then((didOpenSettings) => {
      if (!didOpenSettings) {
        setIsAwaitingHostPermission(false)
      }
    })
  }

  const isPartnerAllowedToOpenAppSettings =
    partnerIdentifier === PARTNER_IDENTIFIERS.VTM ||
    partnerIdentifier === PARTNER_IDENTIFIERS.IZOTA

  return (
    <Modal
      isOpen={shouldShowPopup}
      className="fixed inset-0 z-[10005] flex items-center justify-center font-display"
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          dismissPopup()
        }
      }}
    >
      <Backdrop />
      <Dialog
        aria-label="Location permission request"
        className="z-[200] w-[92%] max-w-[400px] outline-none focus:outline-none"
      >
        <div>
          <div className="layout-animation rounded-lg bg-white p-2 duration-300">
            <img
              src="/images/img-ask-locaiton.webp"
              alt="Location permission"
              className="rounded-sm"
            />

            <div className="mt-5 mb-2 flex flex-col gap-4 px-2 py-1">
              <span className="text-gray-900 text-sm">
                {t("home.request_location.description")}
              </span>

              <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2">
                <Button
                  className={cn(
                    "h-9 rounded-md",
                    isNoCoordinates || !isPartnerAllowedToOpenAppSettings
                      ? "col-span-2"
                      : "border-gray-400/30 bg-gray-200/70"
                  )}
                  variant={isNoCoordinates ? "default" : "outline"}
                  onClick={handleManualLocationClick}
                >
                  {t("input_location")}
                </Button>

                {!isNoCoordinates && isPartnerAllowedToOpenAppSettings ? (
                  <Button
                    className="h-9"
                    disabled={isFetching || isAwaitingHostPermission}
                    onClick={handleAutoLocationClick}
                  >
                    {t("common_next")}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </Modal>
  )
}
